import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiClient, ApiResult } from "../api/apiClient";
import { useApiClient } from "./useApiClient";
import { useDidUnmount } from "./useDidUnmount";

type PaginatedApiRequestState<T> = {
  isLoading: boolean;
  isLoadingPage: boolean;
  /** List of ApiResult objects for each page that has been loaded */
  pages: ApiResult<{ Results: T[] }>[];
};

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const err = error as { name?: string; code?: string; message?: string };
  return (
    err.name === "CanceledError" ||
    err.name === "AbortError" ||
    err.code === "ERR_CANCELED" ||
    err.message === "canceled"
  );
};

export const usePaginatedApiRequest = <TModelType>(
  fn: (
    apiClient: ApiClient,
    signal?: AbortSignal
  ) => Promise<ApiResult<{ Results: TModelType[] }>>,
  fnPage: (
    apiClient: ApiClient,
    continuationToken: string,
    signal?: AbortSignal
  ) => Promise<ApiResult<{ Results: TModelType[] }>>,
  deps: any[]
) => {
  const didUnmount = useDidUnmount();
  const apiClient = useApiClient();
  const requestIdRef = useRef(0);
  const nextPageAbortRef = useRef<AbortController | null>(null);

  const [refreshCount, setRefreshCount] = useState(0);
  const refresh = useCallback(
    () => setRefreshCount((count) => count + 1),
    [setRefreshCount]
  );

  const [apiState, setApiState] = useState<
    PaginatedApiRequestState<TModelType>
  >({
    isLoading: true,
    isLoadingPage: false,
    pages: [],
  });

  // Loading the first page
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const abortController = new AbortController();
    nextPageAbortRef.current?.abort();
    nextPageAbortRef.current = null;

    setApiState((state) => ({
      ...state,
      isLoading: true,
      isLoadingPage: false,
    }));
    fn(apiClient, abortController.signal)
      .then((result) => {
        if (
          didUnmount.current ||
          abortController.signal.aborted ||
          requestId !== requestIdRef.current
        ) {
          return;
        }
        setApiState({
          isLoading: false,
          isLoadingPage: false,
          pages: [result],
        });
      })
      .catch((error) => {
        if (
          didUnmount.current ||
          abortController.signal.aborted ||
          requestId !== requestIdRef.current ||
          isAbortError(error)
        ) {
          return;
        }
        setApiState({
          isLoading: false,
          isLoadingPage: false,
          pages: [{ message: "Unknown Error", ok: false }],
        });
      });

    return () => {
      abortController.abort();
    };
  }, [didUnmount, apiClient, setApiState, refreshCount, ...deps]);

  const fetchNextPage: (() => Promise<void>) | null = useMemo(() => {
    const page = apiState.pages[apiState.pages.length - 1];

    if (!page || !page.ok || !page.continuationToken) {
      return null;
    }

    const firstPageRequestId = requestIdRef.current;

    return () => {
      nextPageAbortRef.current?.abort();
      const abortController = new AbortController();
      nextPageAbortRef.current = abortController;
      setApiState((state) => ({ ...state, isLoadingPage: true }));
      return fnPage(apiClient, page.continuationToken!, abortController.signal)
        .then((result) => {
          if (
            didUnmount.current ||
            abortController.signal.aborted ||
            firstPageRequestId !== requestIdRef.current
          ) {
            return;
          }
          setApiState((state) => ({
            ...state,
            isLoadingPage: false,
            pages: [...state.pages, result],
          }));
        })
        .catch((error) => {
          if (
            didUnmount.current ||
            abortController.signal.aborted ||
            firstPageRequestId !== requestIdRef.current ||
            isAbortError(error)
          ) {
            return;
          }
          setApiState((state) => ({
            ...state,
            isLoadingPage: false,
            pages: [...state.pages, { ok: false, message: "Unknown error." }],
          }));
        });
    };
  }, [didUnmount, apiClient, apiState.pages, setApiState]);

  const mergedData: TModelType[] = useMemo(() => {
    return apiState.pages.reduce<TModelType[]>((list, page) => {
      if (page.ok) {
        return list.concat(page.data.Results);
      }
      return list;
    }, []);
  }, [apiState.pages]);

  const error: string | null = useMemo(() => {
    const page = apiState.pages[apiState.pages.length - 1];
    if (page && !page.ok) {
      return page.message;
    } else {
      return null;
    }
  }, [apiState.pages]);

  return {
    refresh,
    items: mergedData,
    fetchNextPage,
    isLoading: apiState.isLoading,
    isLoadingPage: apiState.isLoadingPage,
    error,
  };
};
